import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismBanner,
  PrismButton,
  PrismLoadingBar,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import HotelBenchmarkInsightCompetitorsSmartCompsetTabSmartCompsetDetailsActions from 'lumen/components/hotel/benchmark-insight/competitors/smart-compset/tab-smart-compset-details-actions';
import HotelBenchmarkInsightMarketCompositionMarketCompositionDetails from 'lumen/components/hotel/benchmark-insight/market-composition/market-composition-details';
import type {
  BeniMarketCompositionMarket,
  BeniSmartCompset,
} from 'lumen/models';
import { BENI_SMART_COMPSET_BUILDER_MESSAGE_TYPES } from 'lumen/utils/constants/benchmark-insight/beni-smart-compset-builder-constants';

interface HotelBenchmarkInsightCompetitorsSmartCompsetTabSmartCompsetDetailsSignature {
  Args: {
    smartCompset: BeniSmartCompset;
  };
}

export default class HotelBenchmarkInsightCompetitorsSmartCompsetTabSmartCompsetDetails extends Component<HotelBenchmarkInsightCompetitorsSmartCompsetTabSmartCompsetDetailsSignature> {
  @service declare access: Services['access'];
  @service('benchmark-insight/beni-smart-compset-service')
  declare benchmarkInsightBeniSmartCompsetService: Services['benchmark-insight/beni-smart-compset-service'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  saveSmartCompsetTask = task(
    { drop: true },
    async (isSourceBanner: boolean): Promise<void> => {
      const { smartCompset } = this.args;

      // eslint-disable-next-line @typescript-eslint/unbound-method
      const { displayAlert } = this.benchmarkInsightBeniSmartCompsetService;
      const { name } = smartCompset;

      let isSuccessful = false;
      try {
        await smartCompset.save();

        displayAlert(
          BENI_SMART_COMPSET_BUILDER_MESSAGE_TYPES.SUCCESS_UPDATE,
          true,
          name,
        );
        isSuccessful = true;
      } catch (error) {
        displayAlert(
          BENI_SMART_COMPSET_BUILDER_MESSAGE_TYPES.FAIL_UPDATE,
          false,
          name,
        );
        this.errorTracking.captureException(error);
      } finally {
        const eventTrigger = isSourceBanner
          ? 'broken-compset-banner'
          : 'smart-compset-settings';

        this.usageAnalytics.track('Smart Compset Updated', {
          eventTrigger,
          updateSuccessful: isSuccessful,
        });
      }
    },
  );

  get lastUpdatedBy(): string | null {
    const { smartCompset } = this.args;
    const { lastChangedDate, user } = smartCompset;

    if (!lastChangedDate) {
      return null;
    }

    const dateObject = dayjs(lastChangedDate);
    if (!dateObject.isValid()) {
      return null;
    }

    const formattedDate = dateObject.format('MMMM D, YYYY');

    if (user?.trim() === '') {
      return this.intl.t('general.updated_date', {
        date: formattedDate,
      });
    }

    const userName = user ?? this.intl.t('general.lighthouse');

    return this.intl.t(
      'benchmark_insight.competitors.smart_compset.general.updated_date_and_by',
      {
        date: formattedDate,
        user: userName,
      },
    );
  }

  get market(): BeniMarketCompositionMarket | null {
    const { marketComposition } = this.args.smartCompset;

    if (!marketComposition) {
      return null;
    }

    return marketComposition;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get onUpdateClickAction() {
    return {
      label: this.intl.t('benchmark-insight.smart-compset.update'),
      action: (): Promise<void> => this.saveSmartCompsetTask.perform(true),
      disabled: this.saveSmartCompsetTask.isRunning,
      isLoading: this.saveSmartCompsetTask.isRunning,
    };
  }

  get showLoadingBar(): boolean {
    // Only show the loading bar when we're not showing the update warning banner with update CTA button,
    // because the update CTA button already has a loading state.
    return this.args.smartCompset.valid && this.saveSmartCompsetTask.isRunning;
  }

  @action
  onEditClick(): void {
    this.router.transitionTo(
      'hotel.settings.competitors.benchmark.smart-compset.categories',
    );

    this.usageAnalytics.track('Smart Compset Editing', {
      eventTrigger: 'smart-compset-settings',
    });
  }

  <template>
    <div class="tab-smart-compset__details">
      {{#if this.showLoadingBar}}
        <PrismLoadingBar />
      {{/if}}

      <div
        class="tab-smart-compset__details__header flex items-center justify-between"
      >
        <h2>{{@smartCompset.name}}</h2>
        {{#if this.access.hasBenchmarkInsightSmartCompsetEdit}}
          <div class="flex flex-row items-center justify-center gap-200">
            <PrismButton
              @buttonType="primary"
              @iconBefore="edit-pencil"
              @label={{t "general.edit"}}
              @onClick={{this.onEditClick}}
              data-test-id="tab-smart-compset__details__header__button--edit"
            />

            <HotelBenchmarkInsightCompetitorsSmartCompsetTabSmartCompsetDetailsActions
              @saveTask={{this.saveSmartCompsetTask}}
              @smartCompset={{@smartCompset}}
            />
          </div>
        {{/if}}
      </div>

      <div class="divider-line mt-600 mb-600" />

      {{#unless @smartCompset.valid}}
        <PrismBanner
          @bannerMessage={{t
            "benchmark_insight.competitors.smart_compset.warning.banner"
          }}
          @bannerStyle="warning"
          @hasCta={{true}}
          @icon="warning"
          @isOneLine={{true}}
          @primaryAction={{this.onUpdateClickAction}}
          class="mb-600"
        />
      {{/unless}}

      <div class="flex flex-row gap-600">
        <div class="details__container__section flex w-1/2 flex-col gap-100">
          <div class="details__container__section__title">
            <p class="text-300--regular">
              {{t
                "benchmark_insight.competitors.smart_compset.description.title"
              }}
            </p>
          </div>

          <p class="text-200--regular">
            {{t "benchmark_insight.competitors.smart_compset.description.text"}}
          </p>
        </div>

        <div class="details__container__section flex w-1/2 flex-col gap-100">
          <div class="details__container__section__title">
            <p class="text-300--regular">
              {{t "benchmark_insight.competitors.smart_compset.general.title"}}
            </p>
          </div>

          <div class="text-200--regular">
            <p>
              {{t
                "benchmark_insight.competitors.smart_compset.general.created_by"
              }}
            </p>

            {{#if this.lastUpdatedBy}}
              <p>
                {{this.lastUpdatedBy}}
              </p>
            {{/if}}
          </div>
        </div>
      </div>

      {{#if this.market}}
        <HotelBenchmarkInsightMarketCompositionMarketCompositionDetails
          @categories={{@smartCompset.convertedWeights}}
          @market={{this.market}}
        />
      {{/if}}
    </div>
  </template>
}
